import { Button, InputNumber, Segmented } from 'antd';
import type { PaymentMethod, OrderItem } from '../types';
import { formatVND } from '@/utils/format';
import { MONEY_MAX, moneyFormatter, moneyParser, moneyInputKeyDownGuard } from '../utils/moneyInput';
import { NonCashPaymentSection } from './NonCashPaymentSection';
import { computePosTotals } from '../utils/orderItemCalc';

interface Props {
  items: OrderItem[];
  paymentMethod: PaymentMethod;
  onPaymentMethodChange: (m: PaymentMethod) => void;
  onCheckout: () => void;
  checkoutDisabled: boolean;
  cashReceived?: number;
  onCashReceivedChange: (v: number) => void;
  paymentAccountId?: string;
  onPaymentAccountChange: (id: string | undefined) => void;
  nonCashAmount?: number;
  onNonCashAmountChange: (v: number | undefined) => void;
}

const PAYMENT_OPTIONS: { value: PaymentMethod; label: string }[] = [
  { value: 'cash', label: 'Tiền mặt' },
  { value: 'bank', label: 'CK' },
  { value: 'wallet', label: 'Ví ĐT' },
  { value: 'card', label: 'Thẻ TD' },
];

export function PaymentFooter({
  items,
  paymentMethod,
  onPaymentMethodChange,
  onCheckout,
  checkoutDisabled,
  cashReceived,
  onCashReceivedChange,
  paymentAccountId,
  onPaymentAccountChange,
  nonCashAmount,
  onNonCashAmountChange,
}: Props) {
  const { totalWeight, totalGoods, totalDiscount, totalVat, grandTotal } = computePosTotals(items);

  const isCash = paymentMethod === 'cash';
  const change = Math.max(0, (cashReceived ?? 0) - grandTotal);

  return (
    <div style={{ borderTop: '1px solid #e0e0e0', flexShrink: 0, padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, color: '#333' }}>Hình thức thanh toán</div>
        <Segmented
          block
          value={paymentMethod}
          onChange={(v) => onPaymentMethodChange(v as PaymentMethod)}
          options={PAYMENT_OPTIONS}
        />
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <tbody>
          <tr>
            <td style={{ color: '#555', paddingBottom: 4 }}>Tổng trọng lượng:</td>
            <td style={{ textAlign: 'right', fontWeight: 600, paddingBottom: 4 }}>{totalWeight.toFixed(2)}g</td>
          </tr>
          <tr>
            <td style={{ color: '#555', paddingBottom: 4 }}>Tổng tiền hàng:</td>
            <td style={{ textAlign: 'right', fontWeight: 600, paddingBottom: 4 }}>{totalGoods.toLocaleString('vi-VN')}</td>
          </tr>
          <tr>
            <td style={{ color: '#555', paddingBottom: 4 }}>Tổng chiết khấu:</td>
            <td style={{ textAlign: 'right', fontWeight: 600, paddingBottom: 4 }}>{totalDiscount.toLocaleString('vi-VN')}</td>
          </tr>
          <tr>
            <td style={{ color: '#555', paddingBottom: 4 }}>Thuế VAT:</td>
            <td style={{ textAlign: 'right', fontWeight: 600, paddingBottom: 4 }}>{totalVat.toLocaleString('vi-VN')}</td>
          </tr>
          <tr>
            <td style={{ color: '#a4193d', fontWeight: 700, fontSize: 15 }}>Tổng tiền thanh toán:</td>
            <td style={{ textAlign: 'right', fontWeight: 700, fontSize: 15, color: '#a4193d' }}>
              {grandTotal.toLocaleString('vi-VN')}
            </td>
          </tr>
        </tbody>
      </table>

      {isCash ? (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <tbody>
            <tr>
              <td style={{ color: '#555', paddingBottom: 8, width: '45%' }}>Tiền khách đưa:</td>
              <td style={{ paddingBottom: 8 }}>
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  max={MONEY_MAX}
                  value={cashReceived}
                  placeholder="0"
                  formatter={moneyFormatter}
                  parser={moneyParser}
                  onKeyDown={moneyInputKeyDownGuard}
                  onChange={(v) => onCashReceivedChange(v ?? 0)}
                />
              </td>
            </tr>
            <tr>
              <td style={{ color: '#555' }}>Tiền thừa trả khách:</td>
              <td style={{ textAlign: 'right', fontWeight: 700, color: '#52c41a' }}>{formatVND(change)}</td>
            </tr>
          </tbody>
        </table>
      ) : (
        <NonCashPaymentSection
          method={paymentMethod}
          amount={grandTotal}
          accountId={paymentAccountId}
          onAccountChange={onPaymentAccountChange}
          nonCashAmount={nonCashAmount}
          onNonCashAmountChange={onNonCashAmountChange}
          cashReceived={cashReceived}
          onCashReceivedChange={onCashReceivedChange}
        />
      )}

      <Button
        type="primary"
        size="large"
        block
        disabled={checkoutDisabled}
        onClick={onCheckout}
        style={{
          background: checkoutDisabled ? '#bdbdbd' : '#52c41a',
          borderColor: checkoutDisabled ? '#9e9e9e' : '#52c41a',
          color: checkoutDisabled ? '#ffffff' : undefined,
          opacity: checkoutDisabled ? 1 : undefined,
          fontWeight: 700,
          cursor: checkoutDisabled ? 'not-allowed' : 'pointer',
        }}
      >
        Ghi hoá đơn và thanh toán
      </Button>
    </div>
  );
}
